// TemplateAIAssistPanel.tsx
// AI assist prompt + proposal preview (add / rewrite / remove) for template editing.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { aiBuilderAPI } from '../../services/api';
import type { Question } from './TemplateQuestionsEditor';

export interface AssistOperation {
  op: 'add' | 'rewrite' | 'remove';
  client_id?: string;
  reason?: string;
  question?: {
    level01?: string;
    level02?: string;
    level03?: string;
    description?: string;
    question_type?: string;
    mandatory_beneficial?: string;
    short_text?: string;
    long_text?: string;
    answer_options?: Array<{
      option_text: string;
      option_seq: number;
      score?: number;
      action_item?: string | null;
    }>;
  };
}

interface TemplateAIAssistPanelProps {
  questions: Question[];
  onQuestionsApplied: (questions: Question[]) => void;
  /** When set, apply writes through to the persisted template */
  templateId?: number;
  topic?: string;
  industry?: string;
  additionalContext?: string;
  /** Suggested themes from shortfall reporting — clicking fills the prompt */
  suggestedThemes?: string[];
  disabled?: boolean;
  /** Reload questions from server after persisted apply */
  onPersistedApplied?: () => void | Promise<void>;
  /** True while a proposal is open — parent should freeze manual question edits */
  onProposalActiveChange?: (active: boolean) => void;
  /** External prompt seed (e.g. shortfall "add these themes") */
  seedPrompt?: string;
  /** Increment to re-apply seedPrompt / optionally auto-run */
  seedToken?: number;
  /** When seedToken changes, automatically propose changes */
  autoRunOnSeed?: boolean;
}

const opTone = (op: string): 'default' | 'success' | 'warning' | 'error' => {
  if (op === 'add') return 'success';
  if (op === 'rewrite') return 'warning';
  if (op === 'remove') return 'error';
  return 'default';
};

const TemplateAIAssistPanel: React.FC<TemplateAIAssistPanelProps> = ({
  questions,
  onQuestionsApplied,
  templateId,
  topic,
  industry,
  additionalContext,
  suggestedThemes = [],
  disabled = false,
  onPersistedApplied,
  onProposalActiveChange,
  seedPrompt,
  seedToken = 0,
  autoRunOnSeed = false,
}) => {
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [operations, setOperations] = useState<AssistOperation[]>([]);
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [assistSessionId, setAssistSessionId] = useState<string | null>(null);
  const [leftoverThemes, setLeftoverThemes] = useState<string[]>([]);
  const lastSeedToken = React.useRef(0);

  const selectedIndices = useMemo(
    () =>
      Object.entries(selected)
        .filter(([, v]) => v)
        .map(([k]) => parseInt(k, 10)),
    [selected]
  );

  const proposalActive = operations.length > 0 || busy;

  useEffect(() => {
    onProposalActiveChange?.(proposalActive);
    return () => onProposalActiveChange?.(false);
  }, [proposalActive, onProposalActiveChange]);

  const appendThemeToPrompt = (theme: string) => {
    setPrompt((prev) => {
      const addition = `Add questions about ${theme}. Avoid overlap with existing questions.`;
      if (!prev.trim()) return addition;
      return `${prev.trim()}\n${addition}`;
    });
  };

  const pollAssist = async (sessionId: string) => {
    const started = Date.now();
    while (Date.now() - started < 180000) {
      const data = await aiBuilderAPI.getTemplateAIAssistProgress(sessionId);
      if (data.status === 'preview' && data.proposal) {
        const ops: AssistOperation[] = data.proposal.operations || [];
        setOperations(ops);
        setSummary(data.proposal.summary || 'Proposed changes ready for review');
        setLeftoverThemes(data.proposal.suggested_themes || []);
        const initial: Record<number, boolean> = {};
        ops.forEach((_, idx) => {
          initial[idx] = true;
        });
        setSelected(initial);
        setAssistSessionId(sessionId);
        return;
      }
      if (data.status === 'failed') {
        throw new Error(data.error_message || 'AI assist failed');
      }
      await new Promise((r) => setTimeout(r, 1500));
    }
    throw new Error('AI assist timed out. Please try again.');
  };

  const handleRun = async (promptOverride?: string) => {
    const runPrompt = (promptOverride ?? prompt).trim();
    if (!runPrompt || questions.length === 0) return;
    setBusy(true);
    setError(null);
    setSummary(null);
    setOperations([]);
    try {
      const payloadQuestions = questions.map((q, idx) => ({
        client_id: String(q.id ?? q.sequence ?? idx + 1),
        id: q.id,
        sequence: q.sequence ?? idx + 1,
        level01: q.level01,
        level02: q.level02,
        level03: q.level03,
        level04: q.level04,
        level05: q.level05,
        description: q.description,
        question_type: q.question_type,
        mandatory_beneficial: q.mandatory_beneficial,
        short_text: q.short_text,
        long_text: q.long_text,
        options: q.options,
      }));

      const start = await aiBuilderAPI.startTemplateAIAssist({
        prompt: runPrompt,
        questions: payloadQuestions,
        template_id: templateId,
        topic,
        industry,
        additional_context: additionalContext,
      });
      await pollAssist(start.session_id);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'AI assist failed');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!seedToken || seedToken === lastSeedToken.current) return;
    if (!seedPrompt?.trim()) return;
    lastSeedToken.current = seedToken;
    setPrompt(seedPrompt);
    if (autoRunOnSeed) {
      void handleRun(seedPrompt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedToken, seedPrompt, autoRunOnSeed]);

  const handleApply = async () => {
    if (!assistSessionId || selectedIndices.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const result = await aiBuilderAPI.applyTemplateAIAssist(assistSessionId, selectedIndices);
      if (result.mode === 'persisted') {
        if (onPersistedApplied) {
          await onPersistedApplied();
        }
      } else if (result.questions) {
        const mapped: Question[] = result.questions.map((q: any, idx: number) => ({
          id: q.id ?? Date.now() + idx,
          sequence: q.sequence ?? idx + 1,
          level01: q.level01 || 'General',
          level02: q.level02 || '',
          level03: q.level03 || '',
          level04: q.level04 || '',
          level05: q.level05 || '',
          description: q.description || '',
          question_type: q.question_type || 'Yes/No',
          mandatory_beneficial: q.mandatory_beneficial || 'Mandatory',
          short_text: q.short_text || '',
          long_text: q.long_text || '',
          options: (q.options || q.answer_options || []).map((opt: any, oidx: number) => ({
            id: opt.id,
            option_text: opt.option_text || '',
            option_seq: opt.option_seq || oidx + 1,
            score: opt.score ?? 0,
            action_item: opt.action_item || '',
          })),
        }));
        onQuestionsApplied(mapped);
      }
      setOperations([]);
      setSummary(null);
      setAssistSessionId(null);
      setPrompt('');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to apply changes');
    } finally {
      setBusy(false);
    }
  };

  const handleDiscard = () => {
    setOperations([]);
    setSummary(null);
    setAssistSessionId(null);
    setSelected({});
    setLeftoverThemes([]);
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon color="primary" />
          <Typography variant="h6">AI Assist</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Describe what to add, rewrite, or remove. Review the proposal before applying — nothing
          changes until you accept. Manual question editing is paused while a proposal is open.
        </Typography>
        {proposalActive && (
          <Alert severity="info">
            Proposal in progress — finish Apply or Discard before editing questions manually.
          </Alert>
        )}

        {suggestedThemes.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Suggested themes (click to add to prompt)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {suggestedThemes.map((theme) => (
                <Chip
                  key={theme}
                  label={theme}
                  size="small"
                  onClick={() => appendThemeToPrompt(theme)}
                  disabled={disabled || busy}
                />
              ))}
            </Box>
          </Box>
        )}

        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Example: Add inventory policy, replenishment method, safety stock review, and DOA for parameter changes. Remove overlapping planning-meeting questions."
          disabled={disabled || busy}
        />

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={() => void handleRun()}
            disabled={disabled || busy || !prompt.trim() || questions.length === 0}
          >
            {busy ? 'Working…' : 'Propose changes'}
          </Button>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {operations.length > 0 && (
          <>
            <Divider />
            <Alert severity="info">{summary}</Alert>
            <Typography variant="subtitle2">
              Select operations to apply ({selectedIndices.length}/{operations.length})
            </Typography>
            <Stack spacing={1}>
              {operations.map((op, idx) => (
                <Paper key={`${op.op}-${idx}`} variant="outlined" sx={{ p: 1.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!selected[idx]}
                        onChange={(e) =>
                          setSelected((prev) => ({ ...prev, [idx]: e.target.checked }))
                        }
                        disabled={busy}
                      />
                    }
                    label={
                      <Box>
                        <Chip
                          size="small"
                          label={op.op.toUpperCase()}
                          color={opTone(op.op)}
                          sx={{ mr: 1 }}
                        />
                        <Typography variant="body2" component="span">
                          {op.reason || 'No reason provided'}
                        </Typography>
                        {op.op !== 'remove' && op.question?.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {op.question.description}
                          </Typography>
                        )}
                        {op.op !== 'add' && op.client_id && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Target question id: {op.client_id}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </Paper>
              ))}
            </Stack>

            {leftoverThemes.length > 0 && (
              <Alert severity="warning">
                Still potentially uncovered after these changes:{' '}
                {leftoverThemes.join('; ')}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button onClick={handleDiscard} disabled={busy}>
                Discard
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleApply}
                disabled={busy || selectedIndices.length === 0}
              >
                Apply selected
              </Button>
            </Box>
          </>
        )}
      </Stack>
    </Paper>
  );
};

export default TemplateAIAssistPanel;
